import React, {useState, useEffect, useCallback} from 'react';
import {useRouter} from 'next/router';
import Video from 'twilio-video';

import {NotificationContainer} from '../components/Notification';
import AVError from './AVError';
import {
  joinRoom,
  setRoomJoined,
  getUser,
  completeRoom,
} from '../api/api';
import {useAppContext, useNotifications} from '../hooks';
import {SCREENSHARE_TRACK} from '../../../lib/constants/duet';
import {BLOCKED_CAMERA_ERROR, assertNotBlockedCamera} from './videoHelpers';

const SETTINGS_KEY = 'duet_settings';

export function getSettings() {
  try {
    const value = localStorage.getItem(SETTINGS_KEY);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
}

export function setSettings(value) {
  return localStorage.setItem(SETTINGS_KEY, JSON.stringify({
    ...getSettings(),
    ...value,
  }));
}

/** Get the localParticipant's video track, ignoring screensharing if present */
const getVideoTrack = (trackMap) => Array.from(trackMap.values())
  .map((publication) => publication.track)
  .filter((track) => track.name !== SCREENSHARE_TRACK)?.[0];

/** Get the localParticipant's audio track */
const getAudioTrack = (trackMap) => Array.from(trackMap.values())
  .map((publication) => publication.track)?.[0];

export const VideoRoomContext = React.createContext({});

const VideoRoomContainer = ({
  onView,
  onExit,
  children,
  roomName: roomNameProp,
  joinRoomRequest = joinRoom,
  completeRoomRequest = completeRoom,
  setRoomJoinedRequest = setRoomJoined,
}) => {
  const {notifications} = useAppContext();
  const router = useRouter();
  const [token, setToken] = useState(null);
  const [room, setRoom] = useState(null);
  const [remoteParticipant, setRemoteParticipant] = useState(null);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [screenShareOn, setScreenShareOn] = useState(false);
  const [isTogglingVideo, setIsTogglingVideo] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const {setError} = useNotifications();
  const {id: roomNameFromQuery, onDemand} = router.query;
  const roomName = roomNameProp || roomNameFromQuery;

  useEffect(() => {
    if (!roomName) return;
    // Get Twilio video token and data about interview
    joinRoomRequest(roomName, onDemand).then(({videoToken, ...extra}) => {
      setToken(videoToken);
      onView?.({onDemand, ...extra});
    });
  }, [roomName]);

  useEffect(() => {
    const participantConnected = async (participant) => {
      setRemoteParticipant(participant);
      getUser(participant.identity).then(setPartnerInfo);
    };

    const participantDisconnected = (participant) => {
      setRemoteParticipant(null);
    };

    /**
     * Handle when a local track is stopped by native browser controls
     *
     * TODO: Also handle when audio and regular video is stopped. Currently
     * left out because it seemed to be causing random muting.
     */
    const localTrackStopped = (videoRoom) => async (track) => {
      if (track.kind === 'video' && track.name === SCREENSHARE_TRACK) {
        track.stop();
        await videoRoom.localParticipant.unpublishTrack(track);
        setScreenShareOn(false);
      }
    };

    const exitRoom = (videoRoom, error) => {
      // Stop local tracks
      videoRoom.localParticipant.audioTracks.forEach((track) => {
        track.track.stop();
      });
      videoRoom.localParticipant.videoTracks.forEach((track) => {
        track.track.stop();
      });

      if (error && error.code === 53118) {
        onExit?.({roomName, onDemand});
      }
    };

    if (token) {
      const {audioDevice, videoDevice} = getSettings();
      // TODO: Still connect to room even if unable to get audio/video tracks
      Video.createLocalTracks({
        audio: audioDevice ? {deviceId: audioDevice} : true,
        video: videoDevice ? {deviceId: videoDevice, width: 1280, height: 720} : true,
      }).then((localTracks) => {
        assertNotBlockedCamera(localTracks.map((track) => track.mediaStreamTrack));
        return Video.connect(token, {
          name: roomName,
          tracks: localTracks,
        });
      }).then((videoRoom) => {
        setRoom(videoRoom);
        videoRoom.on('participantConnected', participantConnected);
        videoRoom.on('participantDisconnected', participantDisconnected);
        videoRoom.on('disconnected', exitRoom);
        videoRoom.localParticipant.on('trackStopped', localTrackStopped(videoRoom));
        videoRoom.participants.forEach(participantConnected);
        setRoomJoinedRequest(roomName);

        window.addEventListener('beforeunload', () => {
          videoRoom.disconnect();
        });
      }).catch((error) => {
        if (error.name === BLOCKED_CAMERA_ERROR) {
          setError({title: 'This camera is not allowed', body: <AVError />});
          return;
        }
        // TODO: Add UI affordance for this error
        if (!['NotAllowedError', 'NotFoundError'].includes(error.name)) throw error;
      });
    }

    return () => {
      setRoom((currentRoom) => {
        if (currentRoom && currentRoom.localParticipant.state === 'connected') {
          currentRoom.localParticipant.tracks.forEach((trackPublication) => {
            trackPublication.track.stop();
          });
          currentRoom.disconnect();
          return null;
        }
        return currentRoom;
      });
    };
  }, [token]);

  // Mute or unmute the local participant
  const toggleAudio = () => {
    // TODO: room can be null, but toggleAudio button should be disabled in that case
    const audioTrack = getAudioTrack(room.localParticipant.audioTracks);

    if (!audioTrack) {
      setError({title: 'Looks like we\'re having audio issues', body: <AVError />});
      setAudioOn(false);
      return;
    }

    if (audioOn) {
      audioTrack.disable();
      setAudioOn(false);
    } else {
      audioTrack.enable();
      setAudioOn(true);
    }
  };

  // Turn local participant's video on or off. Uses publishTrack and unpublishTrack
  // for the reasons mentioned here: https://github.com/twilio/twilio-video-app-react/issues/180#issuecomment-619703145
  const toggleVideo = async () => {
    if (isTogglingVideo) return;
    setIsTogglingVideo(true);

    try {
      if (videoOn) {
        const videoTrack = getVideoTrack(room.localParticipant.videoTracks);
        videoTrack.stop();
        await room.localParticipant.unpublishTrack(videoTrack);
        setVideoOn(false);
      } else {
        const {videoDevice} = getSettings();
        const track = await Video.createLocalVideoTrack(videoDevice ? {deviceId: videoDevice} : {});
        assertNotBlockedCamera([track.mediaStreamTrack]);
        await room.localParticipant.publishTrack(track);
        setVideoOn(true);
      }
    } catch {
      setError({title: 'Looks like we\'re having video issues', body: <AVError />});
      setVideoOn(false);
    }

    setIsTogglingVideo(false);
  };

  // TODO: Fit screen in frame when screen sharing is toggled
  const toggleScreenShare = useCallback(async () => {
    try {
      if (screenShareOn) {
        const screenTrack = Array.from(room.localParticipant.videoTracks.values())
          .map((publication) => publication.track)
          .find((track) => track.name === SCREENSHARE_TRACK);

        if (!screenTrack) {
          setError({title: 'Looks like we\'re having screen sharing issues', body: <AVError />});
          setScreenShareOn(false);
          return;
        }

        screenTrack.stop();
        await room.localParticipant.unpublishTrack(screenTrack);
        setScreenShareOn(false);
      } else {
        const stream = await navigator.mediaDevices.getDisplayMedia({video: {frameRate: 15}});
        const screenTrack = new Video.LocalVideoTrack(
          stream.getTracks()[0],
          {name: SCREENSHARE_TRACK},
        );
        await room.localParticipant.publishTrack(screenTrack);
        setScreenShareOn(true);
      }
    } catch (error) {
      // TODO: Add UI affordance for this error
      if (!['NotAllowedError', 'NotFoundError'].includes(error.name)) throw error;
    }
  }, [room?.localParticipant, screenShareOn]);

  const handleExit = async () => {
    if (window.confirm('Are you sure you would like to end your interview session?')) {
      if (room) {
        try {
          await completeRoomRequest(room.sid);
        } catch (error) {
          console.error('Failed to complete room', error);
        }
        room.disconnect();
      }
      onExit?.({roomName, onDemand});
    }
  };

  const changeAVSettings = async () => {
    const {audioDevice, videoDevice} = getSettings();
    Video.createLocalTracks({
      audio: audioDevice ? {deviceId: audioDevice} : true,
      video: videoDevice ? {deviceId: videoDevice} : true,
    }).then((localTracks) => {
      assertNotBlockedCamera(localTracks.map((track) => track.mediaStreamTrack));
      const tracks = Array.from(room.localParticipant.tracks.values())
        .map((publication) => publication.track)
        .filter((track) => track !== null);
      room.localParticipant.unpublishTracks(tracks);
      room.localParticipant.publishTracks(localTracks);
    }).catch(() => {
      setError({title: 'Looks like we\'re having AV issues', body: <AVError />});
    });
  };

  return (
    <VideoRoomContext.Provider
      value={{
        roomName,
        isConnected: Boolean(room),
        localParticipant: room?.localParticipant,
        remoteParticipant,
        audioOn,
        videoOn,
        screenShareOn,
        partnerInfo,
        toggleAudio,
        toggleVideo,
        toggleScreenShare,
        changeAVSettings,
        handleExit,
      }}
    >
      {children}
      <NotificationContainer notifications={notifications} />
    </VideoRoomContext.Provider>
  );
};

export default VideoRoomContainer;
